import { Maybe } from 'graphql/jsutils/Maybe'
import React from 'react'
import { FormattedMessage } from 'react-intl'

import { ClientProposalCardsProps } from './ClientProposalCards'
import { ClientProposalCardProps } from '../client-proposal-card/ClientProposalCard'
import { ClientProposalCardVoteWrapper } from '../client-proposal-card-vote-wrapper/ClientProposalCardVoteWrapper'
import {
  ClientProposalPrice,
  ClientProposalPriceAwaitingConfirmation,
  ClientProposalPriceWithDiscount,
} from '../client-proposal-price/client-proposal-price'
import { DisponibilityTag } from '../disponibility-tag/DisponibilityTag'
import { Icon } from '../../atoms'
import { NudgeBanner } from '../../molecules'
import {
  ClientProposalCardsFragment,
  ClientProposalUserActionEnum,
  ClientProposalVoteEnum,
  CurrencyEnum,
  HouseBookingTypeEnum,
} from '../../../lib/graphql'
import { Button } from '../../../shadcn/components/ui/button'
import { cn } from '../../../shadcn/utils'
import { openHubSpotChat } from '../../../utils'

type BaseClientProposalCardFooterProps = {
  onSeeProposalDetails: () => void
  userCurrentAction: ClientProposalUserActionEnum
  houseBookingType: HouseBookingTypeEnum
}

type ClientProposalCardFooterProps =
  | ({
      mode: 'vote'
      downCount: number
      upCount: number
      handleVote: ({
        clientProposalId,
        value,
      }: {
        clientProposalId: string
        value: ClientProposalVoteEnum | null
      }) => Promise<void>
      userVote: ClientProposalVoteEnum | null
      clientProposalId: string
    } & BaseClientProposalCardFooterProps)
  | ({
      mode: 'cta'
      userCurrentAction: ClientProposalUserActionEnum
      onAskQuotation: () => void
      onOpenQuote: () => void
    } & BaseClientProposalCardFooterProps)

export const getClientProposalCardsFooter = (
  props: ClientProposalCardFooterProps,
) => {
  if (props.mode === 'vote') {
    const getVisitorQuoteButton = () => {
      switch (props.userCurrentAction) {
        case ClientProposalUserActionEnum.CAN_CONFIRM_BOOKING:
        case ClientProposalUserActionEnum.CAN_ASK_USER_ADMIN_APPROVAL:
          return (
            <Button
              className='w-full flex-1 text-[var(--clr-action-primary-text)]'
              onClick={props.onSeeProposalDetails}
              dataTestId='show-quotation-button'
            >
              <FormattedMessage
                id='ui.ClientProposalCards.seeQuote'
                defaultMessage='Voir le devis'
              />
            </Button>
          )

        case ClientProposalUserActionEnum.CAN_ASK_QUOTATION:
        case ClientProposalUserActionEnum.WAITING_HOST_QUOTATION:
          if (props.houseBookingType !== HouseBookingTypeEnum.QUOTATION) {
            return (
              <Button
                className='w-full flex-1'
                variant='outline'
                onClick={props.onSeeProposalDetails}
              >
                <FormattedMessage
                  id='ui.ClientProposalCards.seeEstimate'
                  defaultMessage='Voir l’estimation'
                />
              </Button>
            )
          } else {
            return null
          }
        default:
          return null
      }
    }

    return (
      <div
        className={cn('flex flex-col gap-4', '@2xl:flex-row @2xl:justify-end')}
      >
        {getVisitorQuoteButton()}
        <ClientProposalCardVoteWrapper
          downCount={props.downCount}
          upCount={props.upCount}
          handleVote={props.handleVote}
          userVote={props.userVote}
          clientProposalId={props.clientProposalId}
        />
      </div>
    )
  }

  const {
    houseBookingType,
    onAskQuotation,
    onOpenQuote,
    onSeeProposalDetails,
    userCurrentAction,
  } = props

  const getContent = () => {
    switch (userCurrentAction) {
      case ClientProposalUserActionEnum.CLIENT_REQUEST_CANCELLED:
        return (
          <Button disabled={true} variant='ghost' className='w-full'>
            <FormattedMessage
              id='ui.UserCurrentActionButton.clientRequestCancelled'
              defaultMessage='Évènement annulé'
            />
          </Button>
        )
      case ClientProposalUserActionEnum.CAN_CONFIRM_BOOKING:
      case ClientProposalUserActionEnum.CAN_ASK_USER_ADMIN_APPROVAL:
        return (
          <Button
            className='w-full'
            onClick={onOpenQuote}
            dataTestId='show-quotation-button'
          >
            <FormattedMessage
              id='ui.ClientProposalCards.seeQuote'
              defaultMessage='Voir le devis'
            />
          </Button>
        )
      case ClientProposalUserActionEnum.BOOKING_DECLINED_BY_USER_ADMIN:
        return (
          <>
            <Button className='w-full' disabled>
              <Icon name='lock' className='mr-1 h-4 w-4' />
              <FormattedMessage
                id='ui.ClientProposalCards.bookingDeclinedByAdmin'
                defaultMessage='Séjour refusé par l’administrateur'
              />
            </Button>
          </>
        )
      case ClientProposalUserActionEnum.CAN_ASK_QUOTATION:
        if (houseBookingType !== HouseBookingTypeEnum.QUOTATION) {
          return (
            <>
              <Button
                variant='outline'
                className='flex-1'
                onClick={onSeeProposalDetails}
              >
                <FormattedMessage
                  id='ui.ClientProposalCards.seeEstimate'
                  defaultMessage='Voir l’estimation'
                />
              </Button>
              <Button
                dataTestId='user-ask-quotation-button'
                className='flex-1'
                onClick={onAskQuotation}
              >
                <FormattedMessage
                  id='ui.ClientProposalCards.getQuote'
                  defaultMessage='Obtenir un devis'
                />
              </Button>
            </>
          )
        } else {
          return (
            <Button
              dataTestId='user-ask-quotation-button'
              className='w-1/2'
              onClick={onAskQuotation}
            >
              <FormattedMessage
                id='ui.ClientProposalCards.getQuote'
                defaultMessage='Obtenir un devis'
              />
            </Button>
          )
        }
      case ClientProposalUserActionEnum.WAITING_HOST_QUOTATION:
        if (houseBookingType !== HouseBookingTypeEnum.QUOTATION) {
          return (
            <>
              <Button
                className='flex-1'
                variant='outline'
                onClick={onSeeProposalDetails}
              >
                <FormattedMessage
                  id='ui.ClientProposalCards.seeEstimate'
                  defaultMessage='Voir l’estimation'
                />
              </Button>
              <Button
                className='flex-1'
                disabled
                dataTestId='waiting-host-quotation-button'
              >
                <Icon name='renew' className='mr-1 h-4 w-4' />
                <FormattedMessage
                  id='ui.ClientProposalCards.waitingQuote'
                  defaultMessage='En attente du devis'
                />
              </Button>
            </>
          )
        } else {
          return (
            <Button
              className='w-1/2'
              disabled
              dataTestId='waiting-host-quotation-button'
            >
              <Icon name='renew' className='mr-1 h-4 w-4' />
              <FormattedMessage
                id='ui.ClientProposalCards.waitingQuote'
                defaultMessage='En attente du devis'
              />
            </Button>
          )
        }
      case ClientProposalUserActionEnum.BOOKING_DECLINED_BY_HOST: {
        if (houseBookingType !== HouseBookingTypeEnum.QUOTATION) {
          return (
            <>
              <Button
                className='flex-1'
                variant='outline'
                onClick={onSeeProposalDetails}
              >
                <FormattedMessage
                  id='ui.ClientProposalCards.seeEstimate'
                  defaultMessage='Voir l’estimation'
                />
              </Button>
              <Button
                className='flex-1'
                variant='outline'
                onClick={() => openHubSpotChat()}
              >
                <FormattedMessage
                  id='ui.ClientProposalCards.contactAdvisor'
                  defaultMessage='Contacter mon conseiller'
                />
              </Button>
            </>
          )
        } else {
          return (
            <Button
              className='w-1/2'
              variant='outline'
              onClick={() => openHubSpotChat()}
            >
              <FormattedMessage
                id='ui.ClientProposalCards.contactAdvisor'
                defaultMessage='Contacter mon conseiller'
              />
            </Button>
          )
        }
      }
      default:
        return null
    }
  }

  const content = getContent()

  if (content) {
    if (houseBookingType !== HouseBookingTypeEnum.QUOTATION) {
      return <div className='flex gap-2'>{content}</div>
    } else {
      return <div className='flex justify-end'>{content}</div>
    }
  }

  return undefined
}

export const getDisponibilityTagComponent = (
  userCurrentAction: ClientProposalUserActionEnum,
) => {
  switch (userCurrentAction) {
    case ClientProposalUserActionEnum.CAN_ASK_QUOTATION:
    case ClientProposalUserActionEnum.WAITING_HOST_QUOTATION:
      return <DisponibilityTag tag='waiting-confirmation' />
    case ClientProposalUserActionEnum.BOOKING_DECLINED_BY_HOST:
    case ClientProposalUserActionEnum.BOOKING_DECLINED_BY_USER_ADMIN:
      return <DisponibilityTag tag='unavailable' />
    case ClientProposalUserActionEnum.CAN_CONFIRM_BOOKING:
      return <DisponibilityTag tag='available' />
    default:
      return undefined
  }
}

export const getClientProposalPriceComponent = (
  userCurrentAction: ClientProposalUserActionEnum,
  houseBookingType: HouseBookingTypeEnum,
  priceWithoutVat: number,
  discountPriceWithoutVat: number,
  adults: number,
  currency: CurrencyEnum,
) => {
  switch (userCurrentAction) {
    case ClientProposalUserActionEnum.BOOKING_DECLINED_BY_HOST:
    case ClientProposalUserActionEnum.BOOKING_DECLINED_BY_USER_ADMIN:
      return undefined
    case ClientProposalUserActionEnum.CAN_ASK_QUOTATION:
    case ClientProposalUserActionEnum.WAITING_HOST_QUOTATION:
      if (houseBookingType !== HouseBookingTypeEnum.QUOTATION) {
        if (priceWithoutVat === discountPriceWithoutVat) {
          return (
            <ClientProposalPrice
              priceWithoutVat={discountPriceWithoutVat}
              adults={adults}
              isEstimatedPrice={true}
              currency={currency}
            />
          )
        } else {
          return (
            <ClientProposalPriceWithDiscount
              priceWithoutVat={priceWithoutVat}
              discountPriceWithoutVat={discountPriceWithoutVat}
              adults={adults}
              isEstimatedPrice={true}
              currency={currency}
            />
          )
        }
      } else {
        return <ClientProposalPriceAwaitingConfirmation />
      }
    case ClientProposalUserActionEnum.CAN_ASK_USER_ADMIN_APPROVAL:
    case ClientProposalUserActionEnum.CAN_CONFIRM_BOOKING:
      if (priceWithoutVat === discountPriceWithoutVat) {
        return (
          <ClientProposalPrice
            priceWithoutVat={discountPriceWithoutVat}
            adults={adults}
            isEstimatedPrice={false}
            currency={currency}
          />
        )
      } else {
        return (
          <ClientProposalPriceWithDiscount
            priceWithoutVat={priceWithoutVat}
            discountPriceWithoutVat={discountPriceWithoutVat}
            adults={adults}
            isEstimatedPrice={false}
            currency={currency}
          />
        )
      }
    default:
      return undefined
  }
}

export const getClientProposalCardProps = ({
  clientProposal,
  disponibilityTag,
  hoveredProposalId,
  onProposalMouseEnter,
  onProposalMouseLeave,
  features,
  getFooter,
  price,
  onHouseTitleClick,
}: Omit<ClientProposalCardsProps, 'clientProposal'> & {
  clientProposal: ClientProposalCardsFragment
}): ClientProposalCardProps | null => {
  const objKey: 'house' | 'serviceProvider' | null = clientProposal.house
    ? 'house'
    : clientProposal.serviceProvider
      ? 'serviceProvider'
      : null

  const bookingType: Maybe<HouseBookingTypeEnum> = clientProposal.house
    ? clientProposal.houseBookingType
    : clientProposal.serviceProvider
      ? HouseBookingTypeEnum.QUOTATION
      : null

  const entity = objKey ? clientProposal[objKey] : null

  if (!entity || !bookingType) {
    return null
  }

  const clientProposalCardProps: ClientProposalCardProps = {
    dates: {
      startDate: clientProposal.startDate,
      endDate: clientProposal.endDate,
    },
    disponibility: clientProposal.userCurrentAction
      ? disponibilityTag(clientProposal.userCurrentAction)
      : undefined,
    hoverStyle: hoveredProposalId === clientProposal._id ? 'hovered' : 'none',

    containerProps: {
      onMouseEnter: () => onProposalMouseEnter?.(clientProposal._id),
      onMouseLeave: () => onProposalMouseLeave?.(),
    },
    nudge:
      features?.includes('nudges') && clientProposal.nudgeState ? (
        <NudgeBanner nudgeState={clientProposal.nudgeState} />
      ) : undefined,
    region: entity.address?.region ?? '',
    department: entity.address?.department ?? undefined,
    title: entity.title ?? '',
    houseId: entity._id,
    imageUrls: entity.pictures?.map((picture) => picture.uri) ?? [],
    onHouseTitleClick:
      objKey === 'house'
        ? () => onHouseTitleClick?.(clientProposal._id)
        : undefined,
    price:
      clientProposal.userCurrentAction && bookingType
        ? price(
            clientProposal.userCurrentAction,
            bookingType,
            clientProposal.price?.totalUser?.priceWithUserFees
              ?.priceWithoutVat ?? 0,
            clientProposal.price?.totalUser?.priceWithDiscountAndFees
              ?.priceWithoutVat ?? 0,
            clientProposal.adults ?? 0,
            entity.currencyArea.currency ?? CurrencyEnum.EUR,
          )
        : undefined,
    footer:
      getFooter && clientProposal.userCurrentAction && bookingType
        ? getFooter(
            clientProposal.userCurrentAction,
            bookingType,
            clientProposal._id,
            {
              isNonCompliantBudget: clientProposal.isNonCompliantBudget,
              isNonCompliantHouse: clientProposal.isNonCompliantHouse,
            },
            {
              upCount: clientProposal.positiveVoteCount,
              downCount: clientProposal.negativeVoteCount,
              userVote: clientProposal.userVote ?? null,
              voteForEmail: clientProposal.voteForEmail ?? null,
            },
          )
        : undefined,
    isClientProposalCancelled: clientProposal.isClientProposalCancelled,
  }

  return clientProposalCardProps
}
